import { DecisionTreeContent, DecisionTreeNode } from "@amzn/ask-legal-domain";

/**
 * Frontend filtering utility for linkable nodes.
 * This replaces the expensive getLinkableNodes API call with client-side filtering.
 *
 * The filtering logic matches the backend implementation to ensure consistency.
 */

export interface LinkableNode {
    nodeId: string;
    overviewText: string;
    isLeaf: boolean;
    path: string[];
}

/**
 * Filter nodes that can be safely linked from a source node.
 *
 * @param fromNodeId The source node that will link to another node
 * @param content The complete decision tree content
 * @returns Array of linkable nodes with metadata
 */
export function filterLinkableNodes(
    fromNodeId: string,
    content: DecisionTreeContent
): LinkableNode[] {
    const allNodes = content.allNodes;

    // Build node map for O(1) lookups instead of repeated O(n) find()
    const nodeMap = new Map<string, DecisionTreeNode>();
    for (const node of allNodes) {
        nodeMap.set(node.id, node);
    }

    // Build parent map upfront for O(1) parent lookups.
    // Maps each nodeId → array of parent nodeIds.
    // This replaces repeated O(n) findParentNodes calls in buildPath.
    const parentMap = new Map<string, string[]>();
    for (const node of allNodes) {
        if (node.children) {
            for (const child of node.children) {
                const parents = parentMap.get(child.nodeId) || [];
                parents.push(node.id);
                parentMap.set(child.nodeId, parents);
            }
        }
    }

    // Verify the fromNode exists
    const fromNode = nodeMap.get(fromNodeId);
    if (!fromNode) {
        console.error(`Source node ${fromNodeId} not found`);
        return [];
    }

    // Filter nodes that can be safely linked
    const linkableNodes = allNodes.filter(node => {
        // Can't link to self
        if (node.id === fromNodeId) {
            return false;
        }

        // Can't link to deleted nodes
        if (node.deleted) {
            return false;
        }

        // Can't link to ancestors (would create cycle)
        if (DecisionTreeNode.isAncestor(fromNodeId, node.id, allNodes)) {
            return false;
        }

        // Cycle validation: ensure no cycle would be created
        // Note: Linking to descendants is allowed - it creates convergent paths (multiple paths to same node)
        // This is a valid DAG pattern and does not create cycles
        if (!DecisionTreeNode.validateNoCircle(fromNodeId, node.id, allNodes)) {
            return false;
        }

        return true;
    });

    // Build path information for context (follows first parent at each level).
    // Uses pre-built parentMap for O(1) lookups instead of O(n) findParentNodes.
    const buildPath = (nodeId: string): string[] => {
        const path: string[] = [];
        let currentId: string | undefined = nodeId;
        const visited = new Set<string>();

        while (currentId && !visited.has(currentId)) {
            visited.add(currentId);
            const node = nodeMap.get(currentId);
            if (node) {
                path.unshift(node.overviewText);
                const parents = parentMap.get(currentId);
                currentId = parents && parents.length > 0 ? parents[0] : undefined;
            } else {
                break;
            }
        }

        return path;
    };

    // Map to output format
    return linkableNodes.map(node => ({
        nodeId: node.id,
        overviewText: node.overviewText,
        isLeaf: DecisionTreeNode.isLeafNode(node),
        path: buildPath(node.id)
    }));
}